import apiClient from './apiClient';
import {
  ActionCancelScope,
  ActionArtifact,
  ActionRunDetail,
  ActionRunSummary,
  ActionTargetRun,
  PageResponseDto,
} from '../types/actionTypes';

export interface ActionRunsQuery {
  page?: number;
  size?: number;
  q?: string;
  status?: string;
  actionType?: string;
  rtuId?: number;
  from?: string;
  to?: string;
}

export interface CancelActionRunRequest {
  cancelScope: Exclude<ActionCancelScope, 'NONE'>;
  targetRunIds: number[];
  scopeRevision: string;
}

export interface ActionCancellationAccepted {
  actionRunId: number;
  acceptedTargetRunIds: number[];
  status: string;
  alreadyApplied: boolean;
}

const toQueryParams = (query: ActionRunsQuery = {}): URLSearchParams => {
  const params = new URLSearchParams();
  Object.entries(query).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      params.set(key, String(value));
    }
  });
  return params;
};

const withPageFallback = <T,>(data: unknown, fallbackQuery?: ActionRunsQuery): PageResponseDto<T> => {
  if (Array.isArray(data)) {
    return {
      content: data as T[],
      page: fallbackQuery?.page ?? 0,
      size: fallbackQuery?.size ?? data.length,
      totalElements: data.length,
      totalPages: 1,
    };
  }

  const typed = data as Partial<PageResponseDto<T>> | null | undefined;
  const content = typed?.content ?? [];
  return {
    content,
    page: typed?.page ?? fallbackQuery?.page ?? 0,
    size: typed?.size ?? fallbackQuery?.size ?? content.length,
    totalElements: typed?.totalElements ?? content.length,
    totalPages: typed?.totalPages ?? 0,
  };
};

const sanitizeDownloadFileName = (value: string): string => {
  const replaced = Array.from(value, (character) => {
    const codePoint = character.codePointAt(0) ?? 0;
    return codePoint <= 31 || '\\/:*?"<>|'.includes(character) ? '_' : character;
  }).join('');
  return replaced.replace(/^\.+/, '').slice(0, 180) || 'action-artifact';
};

const hasZipSignature = async (blob: Blob): Promise<boolean> => {
  try {
    const signature = new Uint8Array(await blob.slice(0, 4).arrayBuffer());
    return signature.length === 4
      && signature[0] === 0x50
      && signature[1] === 0x4b
      && ((signature[2] === 0x03 && signature[3] === 0x04)
        || (signature[2] === 0x05 && signature[3] === 0x06)
        || (signature[2] === 0x07 && signature[3] === 0x08));
  } catch {
    return false;
  }
};

const withZipExtension = (fileName: string): string => {
  if (fileName.toLowerCase().endsWith('.zip')) return fileName;
  const extensionIndex = fileName.lastIndexOf('.');
  const baseName = extensionIndex > 0 ? fileName.slice(0, extensionIndex) : fileName;
  return `${baseName || 'action-artifact'}.zip`;
};

export const actionRunsService = {
  async fetchActionRuns(query: ActionRunsQuery = {}): Promise<PageResponseDto<ActionRunSummary>> {
    const response = await apiClient.get('/action-runs', { params: toQueryParams(query) });
    return withPageFallback<ActionRunSummary>(response.data, query);
  },

  async fetchActiveActionRuns(query: ActionRunsQuery = {}): Promise<PageResponseDto<ActionRunSummary>> {
    const response = await apiClient.get('/action-runs/active', { params: toQueryParams(query) });
    return withPageFallback<ActionRunSummary>(response.data, query);
  },

  async fetchActionRun(actionRunId: number): Promise<ActionRunDetail> {
    const response = await apiClient.get(`/action-runs/${actionRunId}`);
    return response.data as ActionRunDetail;
  },

  async fetchActionArtifacts(actionRunId: number): Promise<ActionArtifact[]> {
    const response = await apiClient.get(`/action-runs/${actionRunId}/artifacts`);
    return Array.isArray(response.data) ? response.data as ActionArtifact[] : [];
  },

  async fetchRtuTargetRuns(rtuId: number, query: ActionRunsQuery = {}): Promise<PageResponseDto<ActionTargetRun>> {
    const response = await apiClient.get(`/rtus/${rtuId}/action-target-runs`, { params: toQueryParams(query) });
    return withPageFallback<ActionTargetRun>(response.data, query);
  },

  async fetchActionTargetRun(targetRunId: number): Promise<ActionTargetRun> {
    const response = await apiClient.get(`/action-target-runs/${targetRunId}`);
    return response.data as ActionTargetRun;
  },

  async cancelActionRun(actionRunId: number, request: CancelActionRunRequest): Promise<ActionCancellationAccepted> {
    const response = await apiClient.post(`/action-runs/${actionRunId}/cancel`, request);
    return response.data as ActionCancellationAccepted;
  },

  async cancelActionTargetRun(targetRunId: number): Promise<ActionCancellationAccepted> {
    const response = await apiClient.post(`/action-target-runs/${targetRunId}/cancel`);
    return response.data as ActionCancellationAccepted;
  },

  async retryActionTargetRun(targetRunId: number): Promise<ActionTargetRun> {
    const response = await apiClient.post(`/action-target-runs/${targetRunId}/retry`);
    return response.data as ActionTargetRun;
  },

  async downloadArtifact(actionRunId: number, artifact: ActionArtifact): Promise<void> {
    // Always use the authenticated same-origin route. A DTO-provided URL must
    // never be allowed to redirect the user's bearer token to another host.
    const downloadPath = `/action-runs/${actionRunId}/artifacts/${artifact.id}/download`;
    const response = await apiClient.get(downloadPath, { responseType: 'blob' });
    const header = String(response.headers?.['content-disposition'] ?? '');
    const encoded = header.match(/filename\*=UTF-8''([^;]+)/i)?.[1];
    const quoted = header.match(/filename="([^"]+)"/i)?.[1];
    let decodedName: string | undefined;
    if (encoded) {
      try {
        decodedName = decodeURIComponent(encoded);
      } catch {
        decodedName = encoded;
      }
    }
    const rawName = decodedName || quoted || artifact.fileName || `action-${actionRunId}-artifact-${artifact.id}`;
    const blob = response.data as Blob;
    const sanitizedName = sanitizeDownloadFileName(rawName);
    const safeName = await hasZipSignature(blob) ? withZipExtension(sanitizedName) : sanitizedName;
    const url = URL.createObjectURL(blob);
    try {
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = safeName;
      anchor.rel = 'noopener';
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
    } finally {
      URL.revokeObjectURL(url);
    }
  },
};

export default actionRunsService;
